<template>
  <div>
    <div class="mb-1 d-flex align-baseline">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.users") }}:</div>
      <div>
        <div data-testid="ticket-preview-users">
          <span class="body2 mr-1" data-testid="ticket-preview-recipient">{{ recipients[0] }}</span>
          <span
            v-if="recipients.length > 1"
            role="button"
            data-testid="ticket-preview-plus-n-btn"
            class="plus-one-text"
            @click="openListModal(TicketsModal.RECIPIENTS, recipients)"
            >&nbsp;+{{ recipients.length - 1 }}</span
          >
        </div>
      </div>
    </div>

    <users-protection-alert
      v-if="showAddToProtectionBtn"
      class="mb-1"
      :ticket-details="ticketDetails"
      :add-users-not-allowed="addUsersNotAllowed"
      @on-users-add-clicked="addUsers(usersToAdd)"
    />

    <div class="d-flex align-center mb-1">
      <div class="subtitle2 mr-1">{{ $t("ticketDetails.when") }}</div>
      <div class="body2" data-testid="ticket-preview-when">
        {{ getFormattedDateTime(ticketDetails.endTime) }}
      </div>
    </div>

    <div class="d-flex align-center mb-1">
      <div class="subtitle2">{{ $t("general.service") }}:</div>
      <div class="d-flex align-center">
        <v-icon
          v-if="ticketDetails.sections.serviceName === Service.PROXY"
          size="25"
          class="mr-2"
          icon="$accessRestrictions"
        />
        <coro-icon v-else class="service-icon" :icon-name="ticketDetails.sections.serviceName" />
        <span
          class="body2"
          :data-testid="`ticket-preview-service-${ticketDetails.sections.serviceName}`"
        >
          {{ serviceNameTitle }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { useTicketPreviewGeneralInfo } from "@/composables/useTicketPreviewGeneralInfo";
import intersection from "lodash/intersection";
import { arrayToLowerCase } from "@coronet/cui";
import { useI18n } from "vue-i18n";
import { Service } from "@/constants/cloud-apps";
import { TicketsModal, TicketType } from "@/constants/tickets";
import { ProxyDirection } from "@/constants/email-proxy";
import CoroIcon from "@/components/CoroIcon.vue";
import UsersProtectionAlert from "@/components/tickets/ticket-preview-details/UsersProtectionAlert.vue";

export default defineComponent({
  components: { UsersProtectionAlert, CoroIcon },
  props: {
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const { getFormattedDateTime, addUsersNotAllowed, usersToAdd, addUsers, openListModal } =
      useTicketPreviewGeneralInfo(props.ticketDetails);

    const recipients = computed(() => props.ticketDetails.sections?.emailMetaData.recipients ?? []);

    const showAddToProtectionBtn = computed(() => {
      // if processed and not a white ticket, then the action button will be visible (`Re-open ticket`)
      const showActionBtn = props.ticketDetails.actions?.length;

      if (!showActionBtn) return false;

      if (
        props.ticketDetails.eventType === TicketType.EMAIL_PHISHING &&
        props.ticketDetails.potentiallyProtectableUsers?.length
      ) {
        return true;
      }

      return (
        intersection(
          arrayToLowerCase(recipients.value),
          arrayToLowerCase(props.ticketDetails.potentiallyProtectableUsers)
        ).length > 0
      );
    });

    const serviceNameTitle = computed(() => {
      const serviceName = i18n.t(`services.${props.ticketDetails.sections.serviceName}`);
      if (props.ticketDetails.sections.serviceName === Service.PROXY) {
        const direction =
          props.ticketDetails.sections.emailMetaData?.emailProxyDirection === ProxyDirection.INBOUND
            ? i18n.t("emailProxy.directions.inbound")
            : i18n.t("emailProxy.directions.outbound");

        return `${direction} ${i18n.t("general.gateway")}`;
      }
      return serviceName;
    });

    return {
      getFormattedDateTime,
      addUsersNotAllowed,
      usersToAdd,
      addUsers,
      recipients,
      showAddToProtectionBtn,
      serviceNameTitle,
      openListModal,
      TicketsModal,
      Service,
    };
  },
});
</script>

<style lang="scss" scoped>
.service-icon {
  width: 25px;
  height: 25px;
}
</style>
